import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { Checkbox } from '@pesto/ui/components/Checkbox';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { useMemo } from 'react';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../types/modal.types';

import {
  useCloseStatementCycleMutation,
  useGetStatementStudioAuthoringContextQuery,
  usePreviewStatementCycleMutation,
} from '../../__generated__/graphql/api';
import { getLoanRefetchQuery } from '../../components/LoanDetails/getLoanRefetchQuery';
import {
  getDefaultStatementPeriod,
  getStatementPeriodEndError,
  hasStatementSchedule,
} from '../../utils/statementSchedule';
import { createFormComponent } from '../utils/create-form-component';
import { mapToOptionalBoolean, YES_NO_OPTIONS } from '../utils/form-helpers';

const schema = z.object({
  from: z.string().min(1, 'Start date is required'),
  to: z.string().min(1, 'End date is required'),
  options: z.boolean().optional(),
  genTransactions: z.string().optional(),
  genPdf: z.string().optional(),
  ytd: z.string().optional(),
  adb: z.string().optional(),
  minPayment: z.number().optional(),
  creditLimit: z.number().optional(),
  batchSize: z.number().optional(),
  dryRun: z.string().optional(),
});

export const GenerateUserStatementComponent = ({ payload, api }: ModalProps<LoanModalPayload>) => {
  const { loanId } = payload;
  const { handleMutation } = useMutationWithToast();
  const refetchQuery = getLoanRefetchQuery(loanId);
  const [closeStatementCycle, { loading }] = useCloseStatementCycleMutation(refetchQuery);

  const {
    data: context,
    loading: contextLoading,
    error: contextError,
  } = useGetStatementStudioAuthoringContextQuery({
    variables: { loanId },
    skip: !loanId,
    fetchPolicy: 'network-only',
  });

  const loan = context?.loan;

  const [previewStatementCycle, { loading: previewLoading }] = usePreviewStatementCycleMutation({
    fetchPolicy: 'no-cache',
  });

  const scheduleAvailable = hasStatementSchedule(loan);

  const defaultPeriod = useMemo(
    () =>
      loan &&
      getDefaultStatementPeriod({
        asOf: new Date(),
        schedule: loan,
        statements: context?.loanStatements ?? [],
      }),
    [loan, context?.loanStatements],
  );

  const closeSchema = useMemo(
    () =>
      schema.superRefine((data, ctx) => {
        const error = getStatementPeriodEndError(data.to, loan);

        if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: error });

        if (data.from > data.to)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['from'],
            message: 'Start date must be on or before end date.',
          });
      }),
    [loan],
  );

  const handleSubmit = (data: z.infer<typeof schema>) => {
    void handleMutation({
      mutation: async () => {
        const mutate = data.dryRun === 'true' ? previewStatementCycle : closeStatementCycle;

        const result = await mutate({
          variables: {
            loanId,
            closeStatementCycleOptions: {
              from: data.from,
              to: data.to,
              genTransactions: mapToOptionalBoolean(data.genTransactions),
              genPdf: mapToOptionalBoolean(data.genPdf),
              ytd: mapToOptionalBoolean(data.ytd),
              adb: mapToOptionalBoolean(data.adb),
              minPayment: data.minPayment,
              creditLimit: data.creditLimit,
              batchSize: data.batchSize,
              dryRun: mapToOptionalBoolean(data.dryRun),
            },
          },
        });

        return !!result.data?.closeStatementCycle;
      },
      extractSuccess: (success) => success,
      onSuccess: () => api.close(),
      debugLabel: 'close-statement-cycle',
      toast: {
        success: data.dryRun === 'true' ? 'Statement dry run completed' : 'Statement cycle closed successfully',
        error: data.dryRun === 'true' ? 'Statement dry run failed' : 'Failed to close statement cycle',
        loading: data.dryRun === 'true' ? 'Running statement preview...' : 'Closing statement cycle...',
      },
    });
  };

  const config: SmartFormConfig<z.infer<typeof schema>> = useMemo(
    () => ({
      fieldGroups: [
        {
          name: 'dateRange',
          label: 'Date Range',
          fields: [
            {
              name: 'from',
              label: 'Start Date',
              type: 'date',
              required: true,
              defaultValue: defaultPeriod?.from ?? '',
            },
            {
              name: 'to',
              label: 'End Date',
              type: 'date',
              required: true,
              defaultValue: defaultPeriod?.to ?? '',
              helperText: 'Must end immediately before the loan’s frozen generation day. Applies to dry runs too.',
            },
            {
              name: 'options',
              label: 'Additional Options',
              type: 'boolean',
              required: false,
              defaultValue: false,
              as: Checkbox,
            },
          ],
        },
        {
          name: 'additionalOptions',
          label: 'Additional Options',
          required: false,
          conditional: {
            when: {
              field: 'options',
              operator: 'equals',
              value: true,
            },
          },
          fields: [
            {
              name: 'genPdf',
              label: 'Generate PDF',
              type: 'select',
              options: YES_NO_OPTIONS,
              required: false,
            },
            {
              name: 'ytd',
              label: 'YTD',
              type: 'select',
              options: YES_NO_OPTIONS,
              required: false,
            },
            {
              name: 'adb',
              label: 'ADB',
              type: 'select',
              options: YES_NO_OPTIONS,
              required: false,
            },
            {
              name: 'minPayment',
              label: 'Minimum Payment',
              component: CurrencyField,
              componentProps: {
                showCurrencySymbol: true,
              },
              required: false,
            },
            {
              name: 'creditLimit',
              label: 'Credit Limit',
              component: CurrencyField,
              componentProps: {
                showCurrencySymbol: true,
              },
              required: false,
              defaultValue: 0,
            },
            {
              name: 'batchSize',
              label: 'Batch Size',
              type: 'number',
              required: false,
              defaultValue: 0,
            },
            {
              name: 'dryRun',
              label: 'Dry Run',
              type: 'select',
              options: YES_NO_OPTIONS,
              required: false,
            },
          ],
        },
      ],
      schema: closeSchema,
      defaultValues: defaultPeriod ?? { from: '', to: '' },
    }),
    [closeSchema, defaultPeriod],
  );

  if (contextLoading) return <p role="status">Loading loan billing schedule…</p>;

  if (contextError) return <p role="alert">{contextError.message}</p>;

  if (!scheduleAvailable) return <p>This loan has no internally managed billing schedule.</p>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Generation day {loan.statementDay}, payment due day {loan.paymentDueDay}. These loan terms are read-only.
      </p>
      {!defaultPeriod && (
        <p className="text-sm text-muted-foreground">
          No completed, unissued billing period is available. Choose an unissued period.
        </p>
      )}
      {createFormComponent({ config, handleSubmit, loading: loading || previewLoading })}
    </div>
  );
};
